import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './VisualMappingEditor.css';

// Configure PDF.js worker (required for react-pdf)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Sample values shown for each field when placing on PDF
const FIELD_SAMPLES = {
  company_name: 'ABC Company Ltd',
  company_address: '123 Business Street, City, State 12345',
  company_contact: 'John Manager',
  company_phone: '+1 234 567 8900',
  company_email: 'contact@company.com',
  employee_name: 'Jane Smith',
  employee_code: 'EMP001',
  employee_email: 'jane@company.com',
  employee_phone: '+1 234 567 8901',
  employee_designation: 'Senior Developer',
  employee_joining_date: '15-01-2024',
  current_date: '08-02-2025',
  agreement_date: '08-02-2025',
  day: 'Saturday',
  start_date: '01-02-2025',
  end_date: '31-12-2025',
  salary: '50,000',
  terms: 'Standard terms apply',
  working_hours: '9:00 AM to 6:00 PM',
  working_days: 'Monday to Saturday And Sunday off',
  replacement_working_days: '8',
  payment_before_date: '5',
  service_provider: 'Khodal Housekeeping Agency',
  service_provider_address: '123 Agency Street, City, State 12345',
  service_provider_represented_by: 'Mr. Agency Representative',
  current_address: '123 Business Street, City, State 12345',
  subject: 'Service Agreement',
  re_agreement_date: '08-02-2026',
  for_agency: 'For Agency',
  for_client: 'For Client',
  agreement_validity_from: '01-02-2025',
  agreement_validity_to: '31-12-2025'
};

const FIELD_KEYS = [
  'company_name', 'company_address', 'company_contact', 'company_phone', 'company_email',
  'employee_name', 'employee_code', 'employee_email', 'employee_phone', 'employee_designation', 'employee_joining_date',
  'current_date', 'agreement_date', 'day',
  'start_date', 'end_date', 'agreement_validity_from', 'agreement_validity_to',
  'salary', 'terms',
  'working_hours', 'working_days', 'replacement_working_days', 'payment_before_date',
  'service_provider', 'service_provider_address', 'service_provider_represented_by', 'current_address',
  'subject',
  're_agreement_date', 'for_agency', 'for_client'
];

function VisualMappingEditor({ template, pdfUrl, existingMappings = [], onSave, onCancel }) {
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [mappings, setMappings] = useState(() => {
    const map = {};
    (existingMappings || []).forEach(m => {
      const key = m.field_key || m.field;
      if (!key) return;
      const x = Number(m.x_position);
      const y = Number(m.y_position);
      if (Number.isNaN(x) || Number.isNaN(y)) return;
      map[key] = { ...m, field_key: key, x_position: x, y_position: y };
    });
    return map;
  });
  const [scale] = useState(1); // 100% zoom for accurate X/Y mapping
  const [fontSize, setFontSize] = useState(12);
  const [fontWeight, setFontWeight] = useState('normal');
  const [pageDimensions, setPageDimensions] = useState({ width: 595, height: 842 }); // A4 default; updated from actual PDF
  const [draggingField, setDraggingField] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });
  const hasDraggedRef = useRef(false);
  const pageContainerRef = useRef(null);
  const markersOverlayRef = useRef(null);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => setNumPages(n), []);

  const onPageLoadSuccess = useCallback((page) => {
    let w = 595, h = 842;
    try {
      const vp = page.getViewport ? page.getViewport({ scale: 1 }) : null;
      if (vp) { w = vp.width; h = vp.height; }
      else if (page.originalWidth) { w = page.originalWidth; h = page.originalHeight; }
    } catch (_) { /* use defaults */ }
    setPageDimensions(prev => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
  }, []);

  const handlePageClick = useCallback((e) => {
    if (!selectedField) return;
    e.stopPropagation();

    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const clickX = (typeof e.offsetX === 'number' ? e.offsetX : e.clientX - rect.left);
    const clickY = (typeof e.offsetY === 'number' ? e.offsetY : e.clientY - rect.top);

    // PDF coordinates: origin at bottom-left, units in points (use actual PDF dimensions)
    const pageWidthPt = pageDimensions.width;
    const pageHeightPt = pageDimensions.height;
    const displayHeight = pageHeightPt * scale;
    const displayWidth = pageWidthPt * scale;

    if (clickX < 0 || clickX > displayWidth || clickY < 0 || clickY > displayHeight) return;

    const pdfX = clickX / scale;
    const pdfY = pageHeightPt - clickY / scale;
    if (Number.isNaN(pdfX) || Number.isNaN(pdfY)) return;

    setMappings(prev => ({
      ...prev,
      [selectedField]: {
        field_key: selectedField,
        page_number: pageNumber,
        x_position: Math.round(pdfX * 10) / 10,
        y_position: Math.round(pdfY * 10) / 10,
        font_size: fontSize,
        font_weight: fontWeight,
        sample_value: FIELD_SAMPLES[selectedField]
      }
    }));
    setSelectedField(null);
  }, [selectedField, pageNumber, scale, fontSize, fontWeight, pageDimensions]);

  const removeMapping = (fieldKey) => {
    setMappings(prev => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  // Drag marker on PDF, or click to select for repositioning (no need to use Re-place)
  const handleMarkerMouseDown = useCallback((e, fieldKey) => {
    e.stopPropagation();
    const m = mappings[fieldKey];
    if (!m || m.page_number !== pageNumber) return;
    const overlay = markersOverlayRef.current;
    if (!overlay) return;
    const rect = overlay.getBoundingClientRect();
    const pageHeightPt = pageDimensions.height;
    const anchorX = (m.x_position || 0) * scale;
    const anchorY = pageHeightPt * scale - (m.y_position || 0) * scale;
    const anchorScreenX = rect.left + anchorX;
    const anchorScreenY = rect.top + anchorY;
    dragOffsetRef.current = {
      x: e.clientX - anchorScreenX,
      y: e.clientY - anchorScreenY
    };
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    hasDraggedRef.current = false;
    setDraggingField(fieldKey);
  }, [mappings, pageNumber, scale, pageDimensions]);

  useEffect(() => {
    if (!draggingField) return;
    const pageHeightPt = pageDimensions.height;
    const pageWidthPt = pageDimensions.width;
    const handleMove = (e) => {
      const dx = Math.abs(e.clientX - dragStartRef.current.x);
      const dy = Math.abs(e.clientY - dragStartRef.current.y);
      if (dx > 3 || dy > 3) hasDraggedRef.current = true;
      if (!hasDraggedRef.current) return;
      const overlay = markersOverlayRef.current;
      if (!overlay) return;
      const rect = overlay.getBoundingClientRect();
      const { x: offX, y: offY } = dragOffsetRef.current;
      const newAnchorScreenX = e.clientX - offX;
      const newAnchorScreenY = e.clientY - offY;
      const relX = newAnchorScreenX - rect.left;
      const relY = newAnchorScreenY - rect.top;
      if (relX < 0 || relX > pageWidthPt * scale || relY < 0 || relY > pageHeightPt * scale) return;
      const pdfX = relX / scale;
      const pdfY = pageHeightPt - relY / scale;
      setMappings(prev => ({
        ...prev,
        [draggingField]: {
          ...prev[draggingField],
          field_key: draggingField,
          x_position: Math.round(pdfX * 10) / 10,
          y_position: Math.round(pdfY * 10) / 10,
          page_number: pageNumber
        }
      }));
    };
    const handleUp = () => {
      if (!hasDraggedRef.current) {
        // Click without drag: select for repositioning (same as Re-place)
        setSelectedField(draggingField);
        const m = mappings[draggingField];
        if (m) {
          setFontSize(m.font_size ?? 12);
          setFontWeight(m.font_weight ?? 'normal');
        }
      }
      setDraggingField(null);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [draggingField, pageNumber, scale, mappings, pageDimensions]);

  const handleSave = () => {
    const payload = Object.values(mappings)
      .filter(m => m.field_key)
      .map(m => {
        const x = Number(m.x_position);
        const y = Number(m.y_position);
        return { ...m, x_position: x, y_position: y };
      })
      .filter(m => !Number.isNaN(m.x_position) && !Number.isNaN(m.y_position))
      .map(m => ({
        field_key: m.field_key,
        page_number: m.page_number || 1,
        x_position: m.x_position,
        y_position: m.y_position,
        font_size: m.font_size || 12,
        font_weight: m.font_weight || 'normal'
      }));
    onSave(payload);
  };

  return (
    <div className="visual-mapping-editor">
      <h3>Field Mappings: {template?.name}</h3>
      <p className="visual-mapping-instruction">
        Select a field from the list, then <strong>click on the PDF</strong> where the top of the text should appear. 
        Or <strong>drag</strong> placed markers directly on the PDF to adjust positions—no need to click Re-place.
        Template PDF should be <strong>A4</strong> (595 × 842 pt) for accurate mapping.
      </p>

      <div className="visual-mapping-layout">
        <div className="field-list-panel">
          <h4>Fields to Map</h4>
          <div className="field-list">
            {FIELD_KEYS.map(key => {
              const m = mappings[key];
              const isSelected = selectedField === key;
              return (
                <div
                  key={key}
                  className={`field-item ${isSelected ? 'selected' : ''} ${m ? 'placed' : ''}`}
                >
                  <div className="field-info">
                    <span className="field-label">{key.replace(/_/g, ' ')}</span>
                    <span className="field-sample">{FIELD_SAMPLES[key] || '(sample)'}</span>
                    {m && (() => {
                      const x = Number(m.x_position);
                      const y = Number(m.y_position);
                      const valid = !Number.isNaN(x) && !Number.isNaN(y);
                      return (
                        <span className="field-position">
                          Page {m.page_number} @ {valid ? `(${x.toFixed(0)}, ${y.toFixed(0)})` : '(click to set)'}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="field-actions">
                    {m ? (
                      <>
                        <button
                          type="button"
                          className="btn-place"
                          onClick={() => setSelectedField(key)}
                        >
                          Re-place
                        </button>
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => removeMapping(key)}
                        >
                          Remove
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn-place"
                        onClick={() => setSelectedField(key)}
                      >
                        Place on PDF
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mapping-options">
            <label>Font size</label>
            <input
              type="number"
              min={6}
              max={24}
              value={fontSize}
              onChange={e => setFontSize(Number(e.target.value) || 12)}
            />
            <label>Font weight</label>
            <select value={fontWeight} onChange={e => setFontWeight(e.target.value)}>
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
            </select>
          </div>
        </div>

        <div className="pdf-panel">
          <h4>PDF Preview – Click to set position</h4>
          <div className="pdf-toolbar">
            <button
              type="button"
              disabled={pageNumber <= 1}
              onClick={() => setPageNumber(p => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <span>Page {pageNumber} of {numPages || '?'}</span>
            <button
              type="button"
              disabled={pageNumber >= (numPages || 1)}
              onClick={() => setPageNumber(p => Math.min(numPages || 1, p + 1))}
            >
              Next
            </button>
            <span className="pdf-zoom-fixed">Zoom: 100%</span>
          </div>

          <div
            ref={pageContainerRef}
            className={`pdf-container ${selectedField ? 'placement-mode' : ''}`}
            style={{ cursor: selectedField ? 'crosshair' : 'default' }}
          >
            {selectedField && (
              <div className="placement-hint">Click on PDF to place &quot;{selectedField.replace(/_/g, ' ')}&quot;</div>
            )}
            <div className="pdf-document-wrapper">
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<div className="pdf-loading">Loading PDF…</div>}
                error={<div className="pdf-error">Failed to load PDF</div>}
              >
                <Page
                  pageNumber={pageNumber}
                  width={pageDimensions.width * scale}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  onLoadSuccess={onPageLoadSuccess}
                />
              </Document>
              {selectedField && (
                <div
                  className="click-capture-overlay"
                  style={{ width: pageDimensions.width * scale, height: pageDimensions.height * scale }}
                  onClick={handlePageClick}
                  title={`Click to place "${selectedField.replace(/_/g, ' ')}"`}
                />
              )}
              <div
                ref={markersOverlayRef}
                className="mapping-markers-overlay"
                style={{ width: pageDimensions.width * scale, height: pageDimensions.height * scale }}
              >
                {Object.entries(mappings).map(([key, m]) =>
                  m.page_number === pageNumber ? (
                    <div
                      key={key}
                      className={`mapping-marker ${draggingField === key ? 'dragging' : ''}`}
                      style={{
                        left: (m.x_position || 0) * scale + 'px',
                        bottom: ((m.y_position || 0) - (m.font_size || 12) * 0.45) * scale + 'px',
                        fontSize: Math.min((m.font_size || 12) * 0.6, 11),
                        fontWeight: m.font_weight === 'bold' ? 'bold' : 'normal'
                      }}
                      title={`${key}: Drag to move, or click to reposition. ${m.sample_value || FIELD_SAMPLES[key]}`}
                      onMouseDown={(e) => handleMarkerMouseDown(e, key)}
                    >
                      {(m.sample_value || FIELD_SAMPLES[key] || key).toString().slice(0, 30)}
                      {(m.sample_value || FIELD_SAMPLES[key] || key).toString().length > 30 ? '…' : ''}
                    </div>
                  ) : null
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="visual-mapping-actions">
        <button type="button" className="btn-primary" onClick={handleSave}>
          Save Mappings
        </button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default VisualMappingEditor;
